import { Columns2 } from 'lucide-react';
import {
  useCallback,
  useRef,
  useState,
  type CSSProperties,
  type DragEvent,
  type ReactNode,
  type RefObject,
} from 'react';
import { useRoutes, type RouteObject } from 'react-router-dom';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { IconButton, Tooltip, cn } from '@/components/ui';
import { errorDetails } from '@/lib/errorReport';
import { StateFrame } from './StateFrame';
import { RightPanelFrame } from './RightPanelFrame';
import type { RightPanelConfig } from './rightPanelContext';
import { TabStrip } from './TabStrip';
import { VIEW_TAB, type PaneId, type TabCrash } from './types';
import { PaneContext, TabContext, useWorkspace } from './workspaceContext';

/*
  WorkspaceCanvas — холст с окнами. Одно окно по умолчанию; второе появляется, когда объект
  открывают рядом (Shift+клик, меню вкладки, Alt+\ или перетаскиванием вкладки вправо).

  Каждая вкладка отрисована своим деревом маршрутов по своему адресу и не размонтируется при
  переключении: прокрутка, введённый текст и раскрытые блоки живут, пока вкладка открыта.
  Граница между окнами тянется мышью и стрелками; двойной клик — поровну.

  У каждой вкладки своя граница ошибок (R42): упала вкладка — на её месте «В этой вкладке сбой» с «Открыть
  заново», остальные вкладки и окна работают. Граница привязана к адресу: ушли со сбойного адреса — вкладка
  снова целая. Упавшая вкладка носит красную точку на ярлыке, пока её не открыли заново.

  notice — строка под полосой вкладок основного окна: «нет связи с сервером» (ConnectionStrip, R42). Слот справа в
  полосе (поиск) она не трогает.

  План дома помощника (spec-3-motion §1): окно — карточка рамки (data-shell-card="pane"), видимая вкладка —
  его прокрутка (data-scrollport), граница окон — зазор, по середине которого идёт стена (data-shell-gap).
*/

interface WorkspaceCanvasProps {
  routes: RouteObject[];
  /** Новая возможность: панель принадлежит вкладке; без свойства прежняя разметка сохраняется. */
  rightPanel?: RightPanelConfig;
  /** Инструменты справа в полосе основного окна (поиск ⌘K). */
  mainTrailing?: ReactNode;
  /** Строка под полосой вкладок основного окна (ConnectionStrip). */
  notice?: ReactNode;
  /** Вкладка упала: запись в трассировку, отклик оболочки. Вкладку уже заменил вид сбоя. */
  onTabError?: (crash: TabCrash) => void;
}

export function WorkspaceCanvas({
  routes,
  mainTrailing,
  notice,
  onTabError,
  rightPanel,
}: WorkspaceCanvasProps) {
  const { state } = useWorkspace();
  const containerRef = useRef<HTMLDivElement>(null);
  const split = Boolean(state.side);

  return (
    <div ref={containerRef} className="relative flex min-w-0 flex-1">
      <Pane
        pane="main"
        routes={routes}
        trailing={mainTrailing}
        notice={notice}
        onTabError={onTabError}
        rightPanel={rightPanel}
        style={split ? { flexBasis: `${state.split * 100}%`, flexGrow: 0 } : undefined}
      />
      {split && <Divider containerRef={containerRef} />}
      {split && <Pane pane="side" routes={routes} onTabError={onTabError} rightPanel={rightPanel} />}
    </div>
  );
}

function Pane({
  pane,
  routes,
  trailing,
  notice,
  onTabError,
  rightPanel,
  style,
}: {
  pane: PaneId;
  routes: RouteObject[];
  trailing?: ReactNode;
  notice?: ReactNode;
  onTabError?: (crash: TabCrash) => void;
  rightPanel?: RightPanelConfig;
  style?: CSSProperties;
}) {
  const ws = useWorkspace();
  const { state, dragging } = ws;
  // Упавшие вкладки окна: id → адрес, на котором упала. Сбой держится, пока вкладка на том же адресе.
  const [crashes, setCrashes] = useState<ReadonlyMap<string, string>>(() => new Map());
  const onCrash = useCallback(
    (crash: TabCrash) => {
      setCrashes((m) => new Map(m).set(crash.id, crash.path));
      onTabError?.(crash);
    },
    [onTabError],
  );
  // «Открыть заново»: забыть сбой, если он про тот же адрес.
  const forget = useCallback(
    (id: string, path: string) =>
      setCrashes((m) => {
        if (m.get(id) !== path) return m;
        const next = new Map(m);
        next.delete(id);
        return next;
      }),
    [],
  );
  const p = pane === 'main' ? state.main : state.side;
  const split = Boolean(state.side);
  if (!p) return null;
  const ids = pane === 'main' ? [VIEW_TAB, ...p.tabs] : p.tabs;
  const pathOf = (id: string) => (id === VIEW_TAB ? state.view : (state.tabs[id]?.path ?? state.view));
  // Вкладку увели с адреса, закрыли или унесли в другое окно — её граница уже новая, сбой забываем.
  const held = [...crashes].filter(([id, at]) => ids.includes(id) && pathOf(id) === at);
  if (held.length !== crashes.size) setCrashes(new Map(held));
  const crashed = new Set(held.map(([id]) => id));

  // Куда можно уронить вкладку: в основном окне без второго — правая половина («рядом»),
  // при двух окнах — всё содержимое чужого окна.
  const dropTarget: PaneId | null = !dragging
    ? null
    : !split && pane === 'main'
      ? 'side'
      : split && dragging.from !== pane
        ? pane
        : null;

  return (
    <section
      aria-label={pane === 'main' ? 'Основное окно' : 'Второе окно'}
      data-shell-card="pane"
      onPointerDownCapture={() => ws.focus(pane)}
      style={style}
      className={cn(
        'relative flex min-w-0 flex-1 flex-col overflow-hidden rounded-lg border border-line bg-canvas shadow-inner-card',
        pane === 'side' && 'animate-fade-in',
      )}
    >
      <TabStrip
        pane={pane}
        focused={state.focused === pane}
        split={split}
        crashed={crashed}
        trailing={
          pane === 'side' ? (
            <Tooltip content="Вернуть вкладки в основное окно · Alt+\">
              <IconButton variant="ghost" size="sm" aria-label="Собрать окна" onClick={ws.unsplit}>
                <Columns2 />
              </IconButton>
            </Tooltip>
          ) : (
            trailing
          )
        }
      />
      {notice}
      <PaneContext.Provider value={pane}>
        <div className="relative min-h-0 flex-1">
          {ids.map((id) => (
            <TabPanel
              key={id}
              id={id}
              pane={pane}
              path={pathOf(id)}
              hidden={p.active !== id}
              routes={routes}
              rightPanel={rightPanel}
              onCrash={onCrash}
              onForget={forget}
            />
          ))}
          {dropTarget && <DropZone target={dropTarget} half={!split} />}
        </div>
      </PaneContext.Provider>
    </section>
  );
}

function TabPanel({
  id,
  pane,
  path,
  hidden,
  routes,
  rightPanel,
  onCrash,
  onForget,
}: {
  id: string;
  pane: PaneId;
  path: string;
  hidden: boolean;
  routes: RouteObject[];
  rightPanel?: RightPanelConfig;
  onCrash: (crash: TabCrash) => void;
  onForget: (id: string, path: string) => void;
}) {
  const element = useRoutes(routes, path);
  return (
    <div
      hidden={hidden}
      data-scrollport={rightPanel ? undefined : ''}
      className={cn('absolute inset-0 overflow-x-hidden', rightPanel ? 'overflow-hidden' : 'overflow-y-auto')}
    >
      <TabContext.Provider value={id}>
        <ErrorBoundary
          resetKey={path}
          onError={(error) => onCrash({ pane, id, path, error })}
          fallback={(error, reset) => (
            <TabCrashFrame
              pane={pane}
              id={id}
              path={path}
              error={error}
              onReopen={() => {
                onForget(id, path);
                reset();
              }}
            />
          )}
        >
          {rightPanel ? (
            <RightPanelFrame
              key={path}
              config={rightPanel}
              compactHeader={rightPanel.compactHeader}
              tabId={id}
              path={path}
              pane={pane}
              hidden={hidden}
            >
              {element}
            </RightPanelFrame>
          ) : (
            element
          )}
        </ErrorBoundary>
      </TabContext.Provider>
    </div>
  );
}

/**
 * Сбой вкладки (spec-2 §3.6): веер цел и неподвижен, кольцо медальона красное и толще. «Открыть заново» —
 * та же вкладка с чистого листа; вкладку объекта можно закрыть, вкладку вида — увести на главную.
 */
function TabCrashFrame({
  pane,
  id,
  path,
  error,
  onReopen,
}: {
  pane: PaneId;
  id: string;
  path: string;
  error: Error;
  onReopen: () => void;
}) {
  const ws = useWorkspace();
  const secondary =
    id !== VIEW_TAB
      ? { label: 'Закрыть вкладку', onClick: () => ws.close(pane, id) }
      : path !== ws.homePath
        ? { label: 'На главную', onClick: () => ws.go(ws.homePath) }
        : undefined;
  return (
    <StateFrame
      kind="crashed"
      title="В этой вкладке сбой"
      description="Остальные вкладки и приложения работают. Ошибка уже записана."
      action={{ label: 'Открыть заново', variant: 'primary', onClick: onReopen }}
      secondaryAction={secondary}
      details={errorDetails(error, path)}
    />
  );
}

function DropZone({ target, half }: { target: PaneId; half: boolean }) {
  const ws = useWorkspace();
  const [over, setOver] = useState(false);
  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    if (ws.dragging) ws.move(ws.dragging.id, target);
    ws.setDragging(null);
    setOver(false);
  };
  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={onDrop}
      className={cn('absolute inset-y-0 right-0 z-raised p-3', half ? 'w-1/2' : 'left-0')}
    >
      <div
        className={cn(
          'grid size-full place-items-center rounded-md border-2 border-dashed text-sm font-medium transition-colors duration-fast',
          over ? 'border-brand bg-brand-soft text-brand-fg' : 'border-line-strong bg-muted text-fg-muted',
        )}
      >
        {target === 'side' ? 'Отпустите — откроется рядом' : 'Отпустите — переедет в это окно'}
      </div>
    </div>
  );
}

function Divider({ containerRef }: { containerRef: RefObject<HTMLDivElement | null> }) {
  const ws = useWorkspace();
  const [active, setActive] = useState(false);
  const frame = useRef(0);
  const ratio = ws.state.split;

  const setFromX = (clientX: number) => {
    const box = containerRef.current?.getBoundingClientRect();
    if (!box) return;
    cancelAnimationFrame(frame.current);
    frame.current = requestAnimationFrame(() => ws.setSplit((clientX - box.left) / box.width));
  };

  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label="Граница окон"
      data-shell-gap=""
      aria-valuemin={30}
      aria-valuemax={70}
      aria-valuenow={Math.round(ratio * 100)}
      tabIndex={0}
      onPointerDown={(e) => {
        e.currentTarget.setPointerCapture(e.pointerId);
        setActive(true);
      }}
      onPointerMove={(e) => {
        if (active) setFromX(e.clientX);
      }}
      onPointerUp={(e) => {
        e.currentTarget.releasePointerCapture(e.pointerId);
        setActive(false);
      }}
      onDoubleClick={() => ws.setSplit(0.5)}
      onKeyDown={(e) => {
        if (e.key === 'ArrowLeft') ws.setSplit(ratio - 0.05);
        else if (e.key === 'ArrowRight') ws.setSplit(ratio + 0.05);
        else return;
        e.preventDefault();
      }}
      className="group/divider relative w-3 shrink-0 cursor-col-resize touch-none focus-visible:outline-none"
    >
      <span
        aria-hidden
        className={cn(
          'absolute inset-y-8 left-1/2 w-0.5 -translate-x-1/2 rounded-full transition-colors duration-fast',
          active
            ? 'bg-brand'
            : 'bg-transparent group-hover/divider:bg-line-strong group-focus-visible/divider:bg-brand',
        )}
      />
    </div>
  );
}
